import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getMovie, getGenres, updateMovie, deleteMovie } from '../../../../api';
import { swalConfig, toastConfig } from '../../../../config';
import useRouteStore from '../../../../hooks/useRouteStore';
import { generateUrl, generateTitle } from '../../../../features/tools.js';

// Components
import Creatable from 'react-select/creatable';
import Input from '../../../../common/Input';
import PosterMedia from '../../../../common/PosterMedia';
import BackdropMedia from '../../../../common/BackdropMedia/BackdropMedia';
import RichTextArea from '../../../../common/RichTextArea';

const AdminMovie = () => {
  const { id } = useParams();
  const mySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const setRoute = useRouteStore((state) => state.setRoute);

  const [movie, setMovie] = useState({});
  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setRoute([
      { name: 'Inicio', url: '/administracion' },
      { name: 'Películas', url: '/administracion/peliculas' },
      { name: movie.titulo_original || 'Película', url: '/administracion/peliculas/' + id },
    ]);
  }, [movie]);

  useEffect(() => {
    getMovie(id).then((res) => {
      setMovie(res.data);
      setLoading(false);
    });
    getGenres().then((res) => {
      setGenres(res.data.map((genre) => ({ value: genre.name, label: genre.name })));
    });
  }, []);

  useEffect(() => {
    if (movie?.generos && genres.length > 0) {
      setSelectedGenres(movie.generos.map((genre) => ({ value: genre, label: genre })));
    }
  }, [genres, movie]);

  const saveMovie = async () => {
    setSaving(true);
    try {
      await updateMovie(id, { ...movie, generos: selectedGenres.map((genre) => genre.value) });
      toast.success('Película actualizada', toastConfig);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al guardar la película', toastConfig);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMovie = async () => {
    const res = await mySwal.fire({ ...swalConfig.confirm, text: `Estás seguro de borrar "${movie.titulo_original}"` });

    if (res.isConfirmed) {
      setDeleting(true);
      try {
        await deleteMovie(id);
        toast.success('Película eliminada', toastConfig);
        navigate('/administracion/peliculas');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Error al eliminar la película', toastConfig);
        setDeleting(false);
      }
    }
  };

  const refresh = () => {
    setLoading(true);
    getMovie(id)
      .then((res) => {
        setMovie(res.data);
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || 'Error al cargar la película', toastConfig);
        setLoading(false);
      });
  };

  return (
    <div className='font-montserrat w-full max-h-full'>
      {!loading ? (
        <>
          <div className='flex items-center justify-between gap-3 pb-1 mb-2 border-b-2'>
            <h1 className='font-semibold text-xl min-w-0 truncate'>Serie: {movie.titulo_original}</h1>
            <div className='flex gap-2 shrink-0'>
              <button
                onClick={saveMovie}
                disabled={saving || deleting}
                className='flex items-center gap-1.5 bg-red-1 text-white font-semibold text-sm px-4 py-1.5 rounded hover:text-red-1 hover:bg-opacity-5 disabled:opacity-50 disabled:cursor-not-allowed transition-all'
              >
                {saving && (
                  <svg className='animate-spin h-3.5 w-3.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                  </svg>
                )}
                Guardar
              </button>
              <button
                onClick={handleDeleteMovie}
                disabled={saving || deleting}
                className='flex items-center gap-1.5 bg-red-1 text-white font-semibold text-sm px-4 py-1.5 rounded hover:text-red-1 hover:bg-opacity-5 disabled:opacity-50 disabled:cursor-not-allowed transition-all'
              >
                {deleting && (
                  <svg className='animate-spin h-3.5 w-3.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                  </svg>
                )}
                Borrar
              </button>
            </div>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4'>
            <Input
              id='titulo_original'
              value={movie.titulo_original}
              onChange={(e) =>
                setMovie({ ...movie, titulo_original: e.target.value, titulo: generateTitle(e.target.value), url: generateUrl(e.target.value) })
              }
              label='Título original'
              labelClassName='w-36'
            />
            <div className='flex items-center gap-2 font-montserrat text-lg'>
              <label htmlFor='genres' className='font-semibold text-red-1 whitespace-nowrap w-36'>
                Géneros
              </label>
              <Creatable
                id='genres'
                isMulti
                options={genres}
                value={selectedGenres}
                onChange={(v) => setSelectedGenres(v)}
                className='w-full relative z-50'
              />
            </div>
            <Input id='titulo' value={movie.titulo} label='Título' labelClassName='w-36' disabled />
            <Input id='url' value={movie.url} label='Url' labelClassName='w-36' disabled />
            <div className='md:col-span-2'>
              <label htmlFor='descripcion' className='font-semibold text-red-1 text-lg'>
                Descripción
              </label>
              <RichTextArea value={movie.descripcion} onChange={(v) => setMovie({...movie, descripcion: v})} />
            </div>
          </div>
          <hr className='my-5 p-0' />
          <div>
            <h2 className='font-semibold text-xl pb-1 mb-2'>Media</h2>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <PosterMedia source={movie} type='movie' refresh={refresh} />
              <BackdropMedia source={movie} type='movie' refresh={refresh} />
            </div>
          </div>
        </>
      ) : (
        <h1 className='font-semibold text-xl'>
          <span className='loader2'></span>
        </h1>
      )}
    </div>
  );
};

export default AdminMovie;
